import { getFileExt } from "@/types/fileUtils";
import { TreeNode } from "@/types/ModelTreeNode";
import { ApsFolderContent, ApsHub, DesignAutomationsClient, ProjectsClient, UpsertModel } from "@api/apiClient";
import axios from "@api/axiosInstance";
import { useHubs } from "@api/hubs";
import { FilesGridTreeDataGroupingCell } from "@components/DataGrid/components/cell/FilesGridTreeDataGroupingCell";
import { GridToolbarCustom } from "@components/DataGrid/components/toolbar/GridToolbarCustom";
import DataGrid from "@components/DataGrid/DataGrid";
import VersionChip from "@components/VersionChip/VersionChip";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import ArticleIcon from "@mui/icons-material/Article";
import { Autocomplete, Box, CircularProgress, Grid } from "@mui/material";
import TextField from "@mui/material/TextField";
import {
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GRID_ROOT_GROUP_ID,
    GRID_STRING_COL_DEF,
    GRID_TREE_DATA_GROUPING_FIELD,
    GridApi,
    GridColDef,
    GridDataSource,
    GridEventListener,
    GridFilterModel,
    GridGroupNode,
    GridRenderCellParams,
    GridRowSelectionPropagation,
    useGridApiRef
} from "@mui/x-data-grid-premium";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import AppStore from "@stores/AppStore";
import { formatBytes } from "@utils/formatBytes";
import { ChangeEvent, forwardRef, MutableRefObject, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";
import ModelVersionSelection from "./ModelVersionSelection";

const slots = {
  toolbar: GridToolbarCustom,
};

const defaultSlotProps = {
  toolbar: {
    showQuickFilter: false,
    showSearchFilter: true,
    printOptions: { disableToolbarButton: true },
    csvOptions: { disableToolbarButton: true },
    excelOptions: { disableToolbarButton: true },

    autosizeColumnsOptions: { disableToolbarButton: true },
    headerFiltersOptions: { disableToolbarButton: true },
  },
};

const defaultInitialState: GridInitialStatePremium = {
  density: "compact",
  pagination: {
    paginationModel: {
      pageSize: 1000,
    },
    rowCount: 0,
  },
};

const getRowId = (row: any) => {
  try {
    return row.key;
  } catch (error) {
    console.error("getRowId error: ", error);
  }
};

const getTreeDataPath = (row: any) => {
  return row.path;
};

const getFileIcon = (row: any) => {
  //TODO: get supported filenames from AppSettings
  switch (row.data.fileExt) {
    case ".rvt":
    case ".dwg":
    case ".ifc":
    case ".xls":
    case ".xlsx":
      return <ArticleIcon color="primary" sx={{ mr: 1 }} />;
    default:
      return <ArticleIcon color="disabled" sx={{ mr: 1 }} />;
  }
};

export const getGroupingColumnDef = (headerName: string): GridColDef => {
  return {
    ...GRID_STRING_COL_DEF,
    type: "custom",
    field: GRID_TREE_DATA_GROUPING_FIELD,
    headerName: headerName,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    disableReorder: true,
    editable: false,
    groupable: false,
    align: "left",
    hideable: false,
    valueGetter: (_value: any, row: any) => {
      return row.label;
    },
    renderCell: (params) => {
      return (
        <FilesGridTreeDataGroupingCell
          {...(params as GridRenderCellParams<any, any, any, GridGroupNode>)}
          slots={{
            fileName: {
              fileIcon: params.rowNode.type === "leaf" ? getFileIcon(params.row) : null,
            },
          }}
        />
      );
    },
  };
};

const isRowSelectable = (node: any) => {
  return node.row.expand || node.row.leaf;
};

const rowSelectionPropagation: GridRowSelectionPropagation = {
  parents: true,
  descendants: true,
};

const projectsClient = new ProjectsClient(undefined, axios);
const designAutomationsClient = new DesignAutomationsClient(undefined, axios);

// Need to clean the API calls

// TODO: refactor this to use react query
const PROJECTS_URL = "/projects/";
const DATA_PROJECTS = "/api/aps/data/projects/";
const FOLDER_CONTENTS = "/folder/contents";
const FOLDER_SEARCH = "/folder/search";

export interface IProjectFormData {
  projectName: string;
  projectId: string;
  description: string;
  accountId: string;
}

export interface AddModelsFormProps {
  carbonToolProjectId?: string;
  handleCloseDialog: (value: boolean) => void;
  onSelectionChange?: (hasSelection: boolean) => void; 
}

const MuiAddModelsForm = forwardRef((props: AddModelsFormProps, ref: any) => {
  const appStore = useAppStore();
  const [key, setKey] = useState(0); // increment key to force re-initialize of the grid
  const carbonToolProjectId = props.carbonToolProjectId;
  const [selectedNodesKeys, setSelectedNodeKeys] = useState<any>({});
  const [expandedKeys, setExpandedKeys] = useState<any>({});
  const [projects, setProjects] = useState<any>([]);
  const [treeNodes, setTreeNodes] = useState<any>([]);
  const [searchNodes, setSearchNodes] = useState<any>([]);
  const [rootFolders, setRootFolders] = useState<any>([]);
  const nodesRef = useRef(null);
  const searchRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedNodes, setSelectedNodes] = useState<TreeNode[]>([]);
  const containerRef = useRef(null);
  const { height } = useResizeObserver({ ref: containerRef });
  const [expanded, setExpanded] = useState(false);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedApsHub, setSelectedApsHub] = useState<ApsHub | null>(null);
  const [selectedApsProject, setSelecteApsProjects] = useState<any | null>(null);
  const [openVersionSelectionDialog, setOpenVersionSelectionDialog] = useState<boolean>(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedVersions, setSelectedVersions] = useState<ApsFolderContent[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [hasSelectedRows, setHasSelectedRows] = useState(false);

  const handleVersionClick = (row: any) => {
    setSelectedItemId(row.key);
    setOpenVersionSelectionDialog(true);
  };

  const apiRef = useGridApiRef();
  const columns = useMemo(() => getColumns(apiRef, appStore, handleVersionClick), [apiRef]);
  const groupingColumnDef = useMemo(() => getGroupingColumnDef(i18n.t("project-form.grid.columns.folders")), [apiRef]);
  const hubs = useHubs();
  const isApsMapped = !!(appStore.project?.apsHubId && appStore.project?.apsProjectId);

  nodesRef.current = treeNodes;
  searchRef.current = searchNodes;

  const initialState = useMemo(() => {
    return { ...defaultInitialState };
  }, []);

  useEffect(() => {
    const apsHub = hubs.data?.find((x) => x.id === appStore.project?.apsHubId) || null;
    if (apsHub) {
      setSelectedApsHub(apsHub);
    }
  }, [hubs.data, appStore.project?.apsHubId]);

  useEffect(() => {
    if (selectedApsHub) {
      handleInputChange({
        target: { name: "accountId", value: selectedApsHub.id },
      } as ChangeEvent<HTMLInputElement>);
      handleAccountChange({
        target: { name: "accountId", value: selectedApsHub.id },
      } as ChangeEvent<HTMLInputElement>);
    }
  }, [selectedApsHub]);

  const [formData, setFormData] = useState<IProjectFormData>({
    projectName: "",
    projectId: "",
    description: "",
    accountId: "",
  });

  useImperativeHandle(ref, () => ({
    onSubmit: () => handleSubmit(),
  }));

  async function getChildData(childLevelObj: any) {
    try {
      const resAssignGrid = await axios.get(DATA_PROJECTS + formData.projectId + FOLDER_CONTENTS + `?hubId=${formData.accountId}&parentId=${childLevelObj.parentId}`);
      if (resAssignGrid.status === 200) {
        return resAssignGrid.data;
      }
    } catch (err) {}
  }

  const processChildData = (nodes: any, path: string[]) => {
    let transformedVal = transFormData(nodes, path);
    return transformedVal;
  };

  async function getParentNodeData(projectId: any) {
    try {
      setLoading(true);
      const resParentNodes = await axios.get(DATA_PROJECTS + projectId + FOLDER_CONTENTS + `?hubId=${formData.accountId}`);
      if (resParentNodes.status === 200) {
        //let rootNodes = transFormData(resParentNodes.data, []);
        //rootNodes = flattenizeTree(rootNodes, []);
        //setTreeNodes(rootNodes);
        setRootFolders(resParentNodes.data);
        return resParentNodes.data;

        //gridApiRef.current.updateRows?.(rootNodes || []);
        //gridApiRef.current?.unstable_applyFilters?.();
        setLoading(false);
      }
    } catch (err) {}
  }

  function transFormData(data: any, path: string[]) {
    console.log(data);
    return data?.map((element: any, index: number) => {
      return {
        key: element.id,
        path: [...path, index.toString()],
        data: {
          id: element.id,
          name:
            // allow localization of specific folder names
            element.name.toLowerCase() === "project files"
              ? i18n.t("project-form.project-files")
              : element.name.toLowerCase() === "plans"
                ? i18n.t("project-form.plans")
                : element.name,
          fileExt: getFileExt(element.name),
          type: element.type,
          viewId: element.viewId,
          viewName: element.viewName,
          storageId: element.storageId,
          createdAt: element.createdAt,
          createdByName: element.createdByName,
          modifiedAt: element.modifiedAt,
          modifiedByName: element.modifiedByName,
          version: element.version,
          fileSize: element.storageSize,
        },
        selectable: element.type !== "folders",
        label: element.name,
        leaf: element.type !== "folders",
        selectedVersion: element.version,
      };
    });
  }

  async function getAccountProjectList(accountId: string) {
    try {
      setLoadingProjects(true);

      const resProjectsAccounts = await axios.get(`/api/aps/data/hubs/${accountId}` + PROJECTS_URL);
      if (resProjectsAccounts.status === 200) {
        setProjects(resProjectsAccounts.data);

        const apsProject = resProjectsAccounts.data.find((x: { id: string | undefined }) => x.id === appStore.project?.apsProjectId) || null;
        if (apsProject != null) {
          setSelecteApsProjects(apsProject);
        }
      }
    } catch (err) {
    } finally {
      setLoadingProjects(false);
    }
  }

  useEffect(() => {
    if (selectedApsProject) {
      setTreeNodes([]);

      handleInputChange({
        target: { name: "projectId", value: selectedApsProject.id },
      } as ChangeEvent<HTMLInputElement>);
      handleProjectChange({
        target: { name: "projectId", value: selectedApsProject.id },
      } as ChangeEvent<HTMLInputElement>);
    }
  }, [apiRef, selectedApsProject]);

  const handleInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
    console.log("handleInputChange: ", e.target.value);
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAccountChange = async (e: ChangeEvent<HTMLInputElement>) => {
    console.log("handleAccountChange: ", e.target.value);
    const apsHub = hubs.data?.find((x) => x.id === e.target.value) || null;
    setSelectedApsHub(apsHub);
    setProjects([]);
    setSelecteApsProjects(null);
    await getAccountProjectList(e.target.value);
  };

  const handleProjectChange = async (e: ChangeEvent<HTMLInputElement>) => {
    console.log("handleProjectChange: ", e.target.value);
    const apsProject = projects.find((x: { id: string | undefined }) => x.id === e.target.value) || null;
    setSelecteApsProjects(apsProject);

    setKey((prev) => prev + 1);
    apiRef.current.unstable_dataSource.cache.clear();
    setTreeNodes([]);
    await getParentNodeData(e.target.value);
  };

  const handleSubmit = async () => {
    console.log(formData);
    setIsSubmitted(true);

    if (formData.accountId.trim().length !== 0 && formData.projectId.trim().length !== 0) {
      try {
        appStore.setIsLoading(true);

        const selectedRows = apiRef.current.getSelectedRows();
        console.log("Selected Rows: ", selectedRows);

        const nodes: TreeNode[] = selectedRows.values().reduce((acc: any, row: any) => {
          if (row.data.type === "items") {
            acc.push(row);
          }
          return acc;
        }, []);

        await updateProjectModels(nodes);

        await appStore.queryClient.invalidateQueries({ queryKey: ["project"] });
      } finally {
        appStore.setIsLoading(false);
        setIsSubmitted(false);
      }
    }
  };

  async function updateProjectModels(nodes: TreeNode[]) {
    console.log("Selected Nodes: ", nodes);
    console.log("ProjectId: ", carbonToolProjectId);

    try {
      props.handleCloseDialog(true);

      const upsertModels = await createPayload(nodes);
      console.log("UpsertModels: ", upsertModels);

      await projectsClient.upsertProjectModels(carbonToolProjectId!, upsertModels);

      // TODO: this should be done as part of the modelPOST?
      await designAutomationsClient.createProjectJobs(carbonToolProjectId!);
    } catch (err) {
      console.log(err);
    }
  }

  function createPayload(selectedNodes: TreeNode[]): UpsertModel[] {
    console.log(selectedNodes);
    console.log(hubs.data);
    console.log(formData);

    return selectedNodes.map((item) => {
      const lastSegmentItem = getLastSegment(item.data.id);

      // TODO: move this up the call stack
      // Check if any selectedVersion's ID includes the last segment of item.data.id
      const selectedVersion = selectedVersions.find((x) => x.id?.includes(lastSegmentItem as any));

      // TODO: something is wrong if this is needed
      // Safeguard against missing properties
      const apsHub = hubs.data?.find((apsHub: ApsHub) => apsHub.id === formData.accountId);
      const apsProject = projects.find((data: any) => data.id === formData.projectId);

      return new UpsertModel({
        projectId: carbonToolProjectId, // Careful, this is the carbon tool project id not the APS project id
        apsHubId: formData.accountId,
        apsHubName: apsHub ? apsHub.name : "Unknown Hub",
        apsProjectId: formData.projectId, //
        apsProjectName: apsProject ? apsProject.name : "Unknown Project",
        apsItemId: selectedVersion ? selectedVersion.id : item.data.id,
        apsItemName: item.data.name,
        apsViewId: item.data.viewId,
        apsViewName: item.data.viewName,
        apsVersion: selectedVersion ? selectedVersion.version : item.data.version,
        apsFileSize: item.data.fileSize,
        apsStorageId: selectedVersion ? selectedVersion.storageId : item.data.storageId,
        apsCreateUserName: selectedVersion ? selectedVersion.createdByName : item.data.createdByName,
        apsLastModifiedUserName: selectedVersion ? selectedVersion.modifiedByName : item.data.modifiedByName,
      });
    });
  }

  const getLastSegment = (str: string) => {
    return str.split(":").pop();
  };

  const handleModelVersionSelectionClose = (selectedItemId: string, selectedVersionData: ApsFolderContent[]) => {
    const newVersion = selectedVersionData.at(-1)?.version;

    const node = treeNodes.find((n: any) => n.key === selectedItemId);
    if (node) {
      node.selectedVersion = newVersion;
      setTreeNodes([...treeNodes]);
    }
    const row = apiRef.current.getRow(selectedItemId);
    // TODO: this causes the item to lose its position in the heirarchy
    // and will cause it to appear at the bottom of the list
    apiRef.current.updateRows?.([{ key: row.key, selectedVersion: newVersion }]);
    apiRef.current.selectRow(selectedItemId, true, false);
    setSelectedVersions(selectedVersionData);
    setOpenVersionSelectionDialog(false);
  };

  const handleRowSelectionChange = useCallback(
    (selection: any) => {
      console.log("Selection Changed:", selection);

      const selectedRows = apiRef?.current?.getSelectedRows() || [];
      console.log("Selected Rows: ", selectedRows);
    
      const hasSelection = selectedRows.size > 0;
      setHasSelectedRows(hasSelection);

      if (props.onSelectionChange) {
        props.onSelectionChange(hasSelection);
      }
    },
    [apiRef?.current?.getSelectedRows, props.onSelectionChange],
  );

  const sx = useMemo(() => {
    return {
      height: (height || 100) - 16,
      maxHeight: (height || 100) - 16,
    };
  }, [height]);

  const isGroupExpandedByDefault = useCallback(
    (node: any) => {
      const ref = nodesRef.current as any;

      let childrenExpanded: boolean = false;
      if (node.id === GRID_ROOT_GROUP_ID) {
        childrenExpanded = true;
      } else {
        const row = ref.find((n: any) => n.key === node.id);
        childrenExpanded = row?.expand || node.depth === 0;
      }

      console.log("isGroupExpandedByDefault: ", childrenExpanded, node);
      return childrenExpanded;
    },
    [ref],
  );

  const searchFolders = useCallback(
    async (filterModel: GridFilterModel) => {
      console.log("searchFolders: ", filterModel);

      try {
        if (filterModel.quickFilterValues?.length === 0 || filterModel.quickFilterValues?.at(0) === "") {
          searchRef.current = [] as any;
          setKey((prev) => prev + 1);
          setTreeNodes([]);
          setSearchNodes([]);

          apiRef.current.unstable_dataSource.cache.clear();
          apiRef.current.unstable_dataSource.fetchRows(GRID_ROOT_GROUP_ID);
          return;
        }

        const results = [];
        for (const parentId of rootFolders.map((n: any) => n.id)) {
          const resParentNodes = await axios.get(
            DATA_PROJECTS + selectedApsProject.id + FOLDER_SEARCH + `?hubId=${formData.accountId}&parentId=${parentId}&searchText=${filterModel.quickFilterValues?.join(" ")}`,
          );
          if (resParentNodes.status === 200) {
            console.log("searchFolders: ", resParentNodes.data);
            results.push(...resParentNodes.data);
          }
        }

        var nodes = processChildData(results, []) as any;
        searchRef.current = nodes;
        setKey((prev) => prev + 1);
        setSearchNodes(nodes);

        apiRef.current.unstable_dataSource.cache.clear();
        apiRef.current.unstable_dataSource.fetchRows(GRID_ROOT_GROUP_ID);
      } catch (err) {
        console.error(err);
      }
    },
    [apiRef, rootFolders, searchRef, selectedApsProject?.id, formData?.accountId],
  );

  useEffect(() => {
    if (searchNodes.length > 0) {
      apiRef.current.unstable_dataSource.cache.clear();
      apiRef.current.unstable_dataSource.fetchRows(GRID_ROOT_GROUP_ID);
    }
  }, [apiRef, searchNodes]);

  const slotProps = useMemo(() => {
    return {
      ...defaultSlotProps,
      toolbar: {
        ...defaultSlotProps.toolbar,
        searchFilterProps: {
          value: searchText,
          expanded: expanded,
          quickSearch: false,
          onExpandedChange: (newExpanded: boolean) => {
            setExpanded(newExpanded);
          },
          onSearchChange: (newSearchValue: string) => {
            console.log("onSearchChange: ", newSearchValue);
            if (newSearchValue !== searchText) {
              setSearchText(newSearchValue);
            }
          },
          onSearchApply: (newSearchValue: string) => {
            console.log("onSearchApply: ", newSearchValue);
            searchFolders({ quickFilterValues: [newSearchValue] } as GridFilterModel);
          },
        },
      },
    };
  }, [searchFolders, setSearchText, searchText, expanded]);

  const dataSource: GridDataSource = useMemo(
    () => ({
      getRows: async (params) => {
        const ref = nodesRef.current as any;
        const searches = searchRef.current as any;
        const rows = { rows: [], rowCount: 0 };

        if (searches.length > 0) {
          setTreeNodes([...searches]);
          return { rows: searches, rowCount: searches.length };
        }

        if (ref.length === 0) {
          if (selectedApsProject?.id) {
            const e = (await getParentNodeData(selectedApsProject.id)) as any;

            var nodes = processChildData(e, []) as any;

            rows.rows = rows.rows.concat(nodes);
            rows.rowCount += nodes.length;

            setTreeNodes([...ref, ...rows.rows]);
            console.log("parent rows: ", rows);
          }
          return rows;
        }

        console.log("get rows: ", params);
        if ((params.groupKeys?.length || 0) > 0) {
          for (const key of params.groupKeys as any) {
            const exists = ref.find((node: any) => node.key === key);
            if (exists.expand) {
              continue;
            }

            const e = (await getChildData({ parentId: key })) as any;

            const parent = ref.find((node: any) => node.key === key);
            parent.expand = true;
            var nodes = processChildData(e, parent.path) as any;

            rows.rows = rows.rows.concat(nodes);
            rows.rowCount += nodes.length;
          }
          if (rows.rows.length > 0) {
            setTreeNodes([...ref, ...rows.rows]);
          }
          console.log("new rows: ", rows);
          return rows;
        }

        rows.rows = ref;
        rows.rowCount = ref.length;

        console.log("all rows: ", rows);
        return rows;
      },
      getGroupKey: (row) => {
        return row.key;
      },
      getChildrenCount: (row) => {
        let rowCount = -1;
        if (row.leaf) {
          rowCount = 0;
        } else {
          rowCount = row.children?.length || 1;
        }
        return rowCount;
      },
    }),
    [apiRef,formData, ref, searchRef, selectedApsProject],
  );

  // TODO: replace this with the new useAutosizeColumns hook
  useEffect(() => {
    const handleRowExpansionChange: GridEventListener<"rowExpansionChange"> = (params: any) => {
      console.log("rowExpansionChange: ", params);

      if (params.childrenExpanded && params.id === GRID_ROOT_GROUP_ID) {
        setTimeout(() => {
          apiRef?.current?.autosizeColumns({
            columns: [GRID_TREE_DATA_GROUPING_FIELD],
            includeOutliers: true,
            includeHeaders: true,
            expand: true,
          });
        }, 0);
      }

      if (params.childrenExpanded && params.depth > 0) {
        const node = treeNodes.find((n: any) => n.key === params.id);
        if (!node?.expand) {
          apiRef?.current?.unstable_dataSource.fetchRows(params.id);
        }
      }
    };

    return apiRef?.current?.subscribeEvent?.("rowExpansionChange", handleRowExpansionChange);
  }, [treeNodes, apiRef?.current?.subscribeEvent]);

  useEffect(() => {
    const handleSortedRowsSet: GridEventListener<"sortedRowsSet"> = (params: any) => {
      console.log("sortedRowsSet: ", params);

      setTimeout(() => {
        apiRef?.current?.autosizeColumns({
          includeOutliers: true,
          includeHeaders: true,
          expand: true,
        });
      }, 0);
    };

    return apiRef?.current?.subscribeEvent?.("sortedRowsSet", handleSortedRowsSet);
  }, [apiRef?.current?.subscribeEvent]);

  useEffect(() => {
    setTimeout(() => {
      apiRef?.current?.autosizeColumns({
        includeOutliers: true,
        includeHeaders: true,
        expand: true,
      });
    }, 0);
  }, [apiRef?.current?.autosizeColumns, treeNodes]);
  // TODO: replace this with the new useAutosizeColumns hook

  useEffect(() => {
    const handleRowsSet: GridEventListener<"rowsSet"> = () => {
      console.log("handleRowsSet");

      // magic
    };

    return apiRef?.current?.subscribeEvent?.("rowsSet", handleRowsSet);
  }, [apiRef?.current?.subscribeEvent]);

  /*
  useEffect(() => {
    if (hubs.data && hubs.data.length > 0 && selectedApsHub == null) {
      setSelectedApsHub(hubs.data[0]);
    }
  }, [hubs.data, selectedApsHub]);

  useEffect(() => {
    if (projects && projects.length > 0 && selectedApsProject == null) {
      setSelecteApsProjects(projects[0]);
    }
  }, [projects,  selectedApsProject]);
  */

  console.log("height: ", height);

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ height: "100%" }}>
      <Grid container spacing={2} sx={{ display: "flex", width: "100%", height: "100%", flexDirection: "column", flexWrap: "nowrap", margin: 0 }}>
        <Grid size={12} sx={{ display: "flex", flexGrow: 0, paddingLeft: 0, paddingRight: 0, paddingTop: 0 }}>
          <Autocomplete
            title={i18n.t("project-form.account-select.title")}
            options={hubs.data!}
            getOptionLabel={(option: any) => option.name}
            value={selectedApsHub}
            disabled={isApsMapped}
            onChange={(_e: any, newValue: any) => {
              handleInputChange({ target: { name: "accountId", value: newValue.id } } as any);
              handleAccountChange({ target: { name: "accountId", value: newValue.id } } as any);
            }}
            isOptionEqualToValue={(option: any, value: any) => option.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                name="accountId"
                label={i18n.t("project-form.account-select.input.title")}
                placeholder={i18n.t("project-form.account-select.input.placeholder")}
                required
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {hubs.isFetching ? <CircularProgress color="inherit" size={20} disableShrink /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            sx={{ marginTop: 2 }}
            fullWidth
          />
        </Grid>
        <Grid size={12} sx={{ display: "flex", flexGrow: 0, paddingLeft: 0, paddingRight: 0 }}>
          <Autocomplete
            title={i18n.t("project-form.project-select.title")}
            options={projects}
            value={selectedApsProject}
            disabled={isApsMapped}
            getOptionLabel={(option: any) => option.name}
            onChange={(_e: any, newValue: any) => {
              handleInputChange({ target: { name: "projectId", value: newValue?.id } } as any);
              handleProjectChange({ target: { name: "projectId", value: newValue?.id } } as any);
            }}
            isOptionEqualToValue={(option: any, value: any) => option.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                name="projectId"
                label={i18n.t("project-form.project-select.input.title")}
                placeholder={i18n.t("project-form.project-select.input.placeholder")}
                required
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingProjects ? <CircularProgress color="inherit" size={20} disableShrink /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            fullWidth
          />
        </Grid>
        <Grid size={12} ref={containerRef} sx={{ display: "flex", flexDirection: "column", flexGrow: 1, position: "relative", paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}>
          <DataGrid
            key={key}
            ref={ref}
            apiRef={apiRef}
            treeData
            checkboxSelection
            autosizeColumns
            disableRowSelectionOnClick
            getTreeDataPath={getTreeDataPath}
            unstable_dataSource={dataSource}
            columns={columns}
            initialState={initialState}
            groupingColDef={groupingColumnDef}
            getRowId={getRowId}
            onRowSelectionModelChange={handleRowSelectionChange}
            rowSelectionPropagation={rowSelectionPropagation}
            slots={slots}
            slotProps={slotProps}
            sx={sx}
            cellSelection
            //filterMode="server"
            //onFilterModelChange={searchFolders}
            pagination
            pageSizeOptions={[1000]}
            throttleRowsMs={1000}
            rowExpander={false}
            disableExpandAndCollapse
            isGroupExpandedByDefault={isGroupExpandedByDefault}
            isRowSelectable={isRowSelectable}
            disableDensitySelector
            disableColumnSelector
            disableColumnFilter
            disableColumnMenu
            disableColumnSorting
            disableMultipleColumnsSorting
            disableColumnReorder
            disableColumnPinning
          />
          {isSubmitted && selectedNodes.length === 0 ? <p style={{ color: "red" }}>{i18n.t("project-form.model-select.error.1")}</p> : <></>}
        </Grid>
      </Grid>
      <ModelVersionSelection
        openVersionDialog={openVersionSelectionDialog}
        projectId={selectedApsProject?.id}
        itemId={selectedItemId as any}
        selectedVersions={selectedVersions}
        onClose={handleModelVersionSelectionClose}
      />
    </Box>
  );
});

export const getColumns = (_apiRef: MutableRefObject<GridApi>, _appStore: AppStore, handleVersionClick: any): GridColDef[] => [
  {
    ...GRID_CHECKBOX_SELECTION_COL_DEF,
    maxWidth: 50,
  },
  /*
  {
    field: "name",
    headerName: i18n.t("project-form.model-select.columns.name"),
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      if (row.data?.type === "items") {
        return row.label;
      }
    },
  },
  */
  /*
  {
    field: "path",
    headerName: i18n.t("project-form.model-select.columns.path"),
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row.path?.join("/");
    },
  },
  */
  /*
  {
    field: "key",
    headerName: i18n.t("project-form.model-select.columns.key"),
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row.key;
    },
  },
  */
  {
    field: "fileSize",
    headerName: i18n.t("project-form.model-select.columns.fileSize"),
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row.data?.type === "items" ? row.data.fileSize : undefined;
    },
    valueFormatter: (value?: number) => {
      return value ? formatBytes(value) : "";
    },
  },
  {
    field: "version",
    headerName: i18n.t("project-form.model-select.columns.version"),
    flex: 1,
    align: "center",
    valueGetter: (_value: any, row: any) => {
      return row?.selectedVersion;
    },
    valueFormatter: (value?: string) => {
      return value ? `V${value}` : value;
    },
    renderCell: (params: GridRenderCellParams) => {
      if (params.row.data?.type === "items") {
        return (
          <VersionChip
            label={params.formattedValue}
            onClick={() => handleVersionClick(params.row)}
            sx={{
              color: params.row.selectedVersion !== params.row.data.version ? "secondary.dark" : undefined,
            }}
          />
        );
      } else {
        return null;
      }
    },
  },
  {
    field: "modifiedAt",
    headerName: i18n.t("project-form.model-select.columns.modifiedAt"),
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row.data?.modifiedAt;
    },
    valueFormatter: (value?: string) => {
      if (value) {
        return i18n.t("datetime_long", { value: new Date(value) });
      }
      return null;
    },
  },

  {
    field: "modifiedByName",
    headerName: i18n.t("project-form.model-select.columns.modifiedByName"),
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row.data?.modifiedByName;
    },
  },
];

const appendPath = (path: string[], key: string | number | undefined) => {
  if (key) {
    path.push(key.toString());
  }
  return path;
};

const flattenizeTree = (nodes: any[] | undefined, path: string[]) => {
  let rows: any[] = [];

  if (nodes) {
    nodes.forEach((node: any, index: number) => {
      // Safety check: skip undefined/null nodes
      if (!node) {
        return;
      }

      path = appendPath(path, `${index}`);
      node.path = [...path];
      // Set depth property for MUI DataGrid
      node.depth = path.length - 1;
      rows.push(node);

      const childNodes = flattenizeTree(node.children, path);
      if (childNodes.length > 0) {
        rows = rows.concat(childNodes);
      }

      path.pop();
    });
  }

  // Filter out any undefined/null rows that might have slipped through
  return rows.filter((row) => row && typeof row === "object");
};

const fileSizeBodyTemplate = (row: any) => {
  return row.data?.type === "items" ? <Box>{formatBytes(row.data.fileSize)}</Box> : <></>;
};

const modifiedAtBodyTemplate = (row: any) => {
  return <div>{i18n.t("datetime_long", { value: new Date(row.data?.modifiedAt) })}</div>;
};

export default MuiAddModelsForm;
